import { useState, useEffect, type RefObject } from 'react'

/**
 * Detects whether the background behind the chatbot button is dark or light.
 * Uses elementsFromPoint to sample all stacked elements, skipping decorative
 * overlays (low effective opacity) and transparent backgrounds.
 *
 * Supports rgb(), rgba(), oklch(), and oklab() color formats (Tailwind CSS v4).
 * Also parses CSS gradients in backgroundImage.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>,
): 'dark' | 'light' {
  const [background, setBackground] = useState<'dark' | 'light'>('light')

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    /**
     * Extract luminance from a CSS color string.
     * Returns 0–1 (0 = black, 1 = white) or null if transparent/unparseable.
     */
    const colorLuminance = (color: string): number | null => {
      if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return null

      // oklch(L C H) or oklch(L C H / alpha)
      const oklchMatch = color.match(/oklch\(\s*([\d.]+)/)
      if (oklchMatch) return parseFloat(oklchMatch[1])

      // oklab(L a b) or oklab(L a b / alpha) — L is perceptual lightness 0–1
      const oklabMatch = color.match(/oklab\(\s*([\d.]+)/)
      if (oklabMatch) {
        // Check alpha
        const alphaMatch = color.match(/\/\s*([\d.]+)/)
        if (alphaMatch && parseFloat(alphaMatch[1]) < 0.3) return null
        return parseFloat(oklabMatch[1])
      }

      // rgb(r, g, b) / rgba(r, g, b, a)
      const rgbMatch = color.match(/[\d.]+/g)
      if (rgbMatch && rgbMatch.length >= 3) {
        const a = rgbMatch.length >= 4 ? parseFloat(rgbMatch[3]) : 1
        if (a < 0.3) return null
        const [r, g, b] = rgbMatch.map(Number)
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255
      }

      return null
    }

    /**
     * Try to extract luminance from a backgroundImage (gradient).
     * Finds all color functions in the gradient and averages their luminance.
     */
    const gradientLuminance = (bgImage: string): number | null => {
      if (!bgImage || bgImage === 'none') return null

      // Match all color functions: rgb(...), rgba(...), oklch(...), oklab(...)
      const colorMatches = bgImage.match(/(rgba?|oklch|oklab)\([^)]+\)/g)
      if (!colorMatches || colorMatches.length === 0) return null

      const luminances: number[] = []
      for (const c of colorMatches) {
        const l = colorLuminance(c)
        if (l !== null) luminances.push(l)
      }

      if (luminances.length === 0) return null
      return luminances.reduce((a, b) => a + b, 0) / luminances.length
    }

    /** Check effective opacity by walking up ancestors */
    const getEffectiveOpacity = (el: Element): number => {
      let opacity = 1
      let current: Element | null = el
      while (current && current !== document.documentElement) {
        opacity *= parseFloat(getComputedStyle(current).opacity)
        if (opacity < 0.1) return 0
        current = current.parentElement
      }
      return opacity
    }

    const check = () => {
      const rect = element.getBoundingClientRect()
      const x = rect.left + rect.width / 2
      const y = rect.top + rect.height / 2

      // Hide the entire chatbot container
      const chatbotContainer = element.parentElement
      if (!chatbotContainer) return
      chatbotContainer.style.visibility = 'hidden'

      const elements = document.elementsFromPoint(x, y)

      chatbotContainer.style.visibility = ''

      for (const el of elements) {
        if (chatbotContainer.contains(el)) continue
        if (getEffectiveOpacity(el) < 0.2) continue

        const style = getComputedStyle(el)

        // Check backgroundColor first
        const bgLum = colorLuminance(style.backgroundColor)
        if (bgLum !== null) {
          setBackground(bgLum < 0.45 ? 'dark' : 'light')
          return
        }

        // Check backgroundImage (gradients)
        const gradLum = gradientLuminance(style.backgroundImage)
        if (gradLum !== null) {
          setBackground(gradLum < 0.45 ? 'dark' : 'light')
          return
        }
      }

      setBackground('light')
    }

    check()

    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })

    const interval = setInterval(check, 500)

    return () => {
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
      clearInterval(interval)
    }
  }, [elementRef])

  return background
}
